/**
 * feat-235 — one Track art slot. Two instances per track (D10): the in-progress
 * state (`image_s3_key`) and the completed state (`completed_image_s3_key`).
 *
 * `LessonImageControl` is the pattern being copied, deliberately down to the
 * details that are easy to drop: the 2 MB / PNG-JPEG-WebP PRE-CHECK before the
 * presign round-trip (S3 hard-enforces both as POST conditions — the client
 * check only buys a friendly message), clearing the file input so re-picking the
 * SAME file fires `onChange` again, and the inert "Upload unavailable in demo
 * mode" state in `fake`, where there is no backend to presign against.
 *
 * NOTHING RENDERS THE COMPLETED ART UNTIL SLICE 3. It is authored now because
 * content is authored once (program-doc invariant 4), which is also why the slot
 * says so instead of looking broken.
 */

import React, { useRef, useState } from 'react';
import { config } from '../../../../config/environment';
import {
  LESSON_IMAGE_ALLOWED_TYPES,
  LESSON_IMAGE_MAX_BYTES,
  trackImagePublicUrl,
  uploadTrackImage,
} from '../../../../services/quizContentAdminService';
import { trackMonogram } from './trackArt';

export const TrackArtControl: React.FC<{
  tenantId: string;
  trackId: string;
  trackTitle: string;
  /** Slot caption, e.g. "While in progress" / "Once completed". */
  caption: string;
  /** What the button says it is acting on, e.g. "in-progress art". The VISIBLE
   *  text is the accessible name — two slots on one panel both saying "Upload"
   *  would be two controls with the same name, which is what an aria-label would
   *  then have to paper over. */
  artLabel: string;
  /** Line under the buttons — what this state is for. */
  hint: string;
  s3Key: string | null;
  /** Persist the key (or `null` to clear) on the right column of the track. */
  onSave: (s3Key: string | null) => Promise<void>;
  /** Shown inside an empty slot when there is a sensible fallback to name. */
  emptyNote?: string;
}> = ({ tenantId, trackId, trackTitle, caption, artLabel, hint, s3Key, onSave, emptyNote }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fakeMode = config.environment === 'fake';

  const imageUrl = trackImagePublicUrl(s3Key);

  const handleSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Clear the input so re-picking the SAME file fires onChange again.
    e.target.value = '';
    if (!file) return;
    setError(null);

    if (!LESSON_IMAGE_ALLOWED_TYPES.includes(file.type)) {
      setError('Unsupported file type. Use a PNG, JPEG, or WebP image.');
      return;
    }
    if (file.size > LESSON_IMAGE_MAX_BYTES) {
      setError('Image is too large. Max size is 2 MB.');
      return;
    }

    setUploading(true);
    try {
      const { s3_key } = await uploadTrackImage(file, tenantId, trackId);
      await onSave(s3_key);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    setError(null);
    setUploading(true);
    try {
      await onSave(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border border-softBorder rounded-2xl bg-white p-3 flex flex-col gap-2 items-start">
      <span className="text-[11px] font-extrabold text-inkMuted tracking-wide uppercase">{caption}</span>

      <div className="w-full aspect-[3/2] rounded-xl border border-softBorder overflow-hidden grid place-items-center bg-cream/60">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={`${trackTitle} — ${caption}`}
            data-testid={`track-art-${trackId}-${artLabel}`}
            className="w-full h-full object-cover"
          />
        ) : emptyNote ? (
          <span className="text-[11.5px] text-inkMuted/90 text-center px-3">{emptyNote}</span>
        ) : (
          <span className="text-2xl font-extrabold text-preview" aria-hidden>
            {trackMonogram(trackTitle)}
          </span>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={handleSelect}
        disabled={uploading || fakeMode}
        aria-label={`${artLabel} file`}
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading || fakeMode}
          className="text-xs font-semibold rounded-full px-3 py-1.5 bg-coral text-white hover:brightness-95 disabled:opacity-50"
        >
          {uploading ? 'Uploading…' : imageUrl ? `Replace ${artLabel}` : `Upload ${artLabel}`}
        </button>
        {imageUrl && (
          <button
            type="button"
            onClick={handleRemove}
            disabled={uploading || fakeMode}
            className="text-xs font-semibold text-coral hover:underline disabled:opacity-50"
          >
            Remove {artLabel}
          </button>
        )}
      </div>

      {error ? (
        <span className="text-[11px] text-danger-text">{error}</span>
      ) : fakeMode ? (
        <span className="text-[11px] text-inkMuted">Upload unavailable in demo mode</span>
      ) : (
        <span className="text-[11px] text-inkMuted">
          {hint} · PNG, JPEG, or WebP · max 2 MB
        </span>
      )}
    </div>
  );
};

export default TrackArtControl;
